import { useMemo, useState } from "react";
import { Button, Combobox, Group, ScrollArea, Text, Tooltip, useCombobox } from "@mantine/core";
import { FiCheck, FiChevronDown, FiFolder } from "react-icons/fi";
import { useNavigate, useMatch } from "react-router-dom";
import { useGetMultiplacerProjectsQuery } from "../openapi";
import { isProjectViewerOnlyProject } from "./projectViewerOnlyProject";
import classes from "./pages/AuthenticatedPages/AuthenticatedPages.module.css";

export function ProjectSwitcher({ width = 240 }: { width?: number | string }) {
  const navigate = useNavigate();
  const match = useMatch("/:projectId/*");
  const projectId = match?.params.projectId ?? null;
  const { data: projects = [], isFetching } = useGetMultiplacerProjectsQuery();
  const [search, setSearch] = useState("");
  const combobox = useCombobox({
    onDropdownOpen: () => combobox.focusSearchInput(),
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      combobox.focusTarget();
      setSearch("");
    },
  });
  const current = projects.find((project) => String(project.projectNo) === projectId);
  const filtered = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    return projects.filter((project) =>
      `${project.title} ${project.projectNo}`.toLocaleLowerCase().includes(query)
    );
  }, [projects, search]);

  return (
    <Combobox
      store={combobox}
      position="bottom-start"
      width={360}
      withinPortal
      onOptionSubmit={(projectNo) => {
        combobox.closeDropdown();
        navigate(`/${projectNo}/${isProjectViewerOnlyProject(projectNo) ? "viewer" : "modules"}`);
      }}
    >
      <Combobox.Target targetType="button">
        <Tooltip
          label={
            current ? `${current.title} (${current.projectNo}) · Bytt prosjekt` : "Velg prosjekt"
          }
          disabled={combobox.dropdownOpened}
          withArrow
        >
          <Button
            variant="default"
            className={classes.navigationButton}
            w={width}
            justify="space-between"
            aria-label={current ? `Bytt prosjekt: ${current.title}` : "Velg prosjekt"}
            leftSection={<FiFolder size={16} aria-hidden="true" />}
            rightSection={<FiChevronDown size={14} aria-hidden="true" />}
            styles={{ label: { minWidth: 0, flex: 1, textAlign: "left" } }}
            onClick={() => combobox.toggleDropdown()}
          >
            <Text component="span" inherit truncate>
              {current?.title ?? (isFetching ? "Laster prosjekter…" : "Velg prosjekt")}
            </Text>
          </Button>
        </Tooltip>
      </Combobox.Target>
      <Combobox.Dropdown style={{ maxWidth: "calc(100vw - 24px)" }}>
        <Combobox.Search
          aria-label="Søk etter prosjekt"
          placeholder="Søk etter navn eller prosjektnummer"
          value={search}
          onChange={(event) => {
            setSearch(event.currentTarget.value);
            combobox.resetSelectedOption();
          }}
        />
        <Combobox.Options>
          <ScrollArea.Autosize mah={320} type="auto">
            {filtered.map((project) => (
              <Combobox.Option
                key={project.projectNo}
                value={String(project.projectNo)}
                active={String(project.projectNo) === projectId}
              >
                <Group wrap="nowrap" justify="space-between" gap="sm">
                  <div style={{ minWidth: 0 }}>
                    <Text size="sm" fw={500} style={{ overflowWrap: "anywhere" }}>
                      {project.title}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {project.projectNo}
                    </Text>
                  </div>
                  {String(project.projectNo) === projectId && (
                    <FiCheck size={16} style={{ flexShrink: 0 }} aria-hidden="true" />
                  )}
                </Group>
              </Combobox.Option>
            ))}
            {filtered.length === 0 && (
              <Combobox.Empty>
                {isFetching ? "Laster prosjekter…" : "Ingen prosjekter funnet"}
              </Combobox.Empty>
            )}
          </ScrollArea.Autosize>
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}
